import { capitalize } from '@angle/utils'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  CenteredSpinner,
  Modal,
  SummaryOfBenefitsCoverage,
  useToaster
} from 'react-angle-dashboard-components'
import { useQuery } from 'react-query'
import { useHistory } from 'react-router-dom'
import PlanList from '../../components/PlanList'
import { useCaptureValue } from '../../hooks/use-capture-question'
import useStep from '../../hooks/use-step'
import { Contribution } from '../../interfaces/contribution'
import { ISelectablePlan } from '../../interfaces/plan'
import { Paths } from '../../routes/paths'
import { planService } from '../../services'
import { GbsStore } from '../../store'
import { GbsStep } from '../../store/interfaces'
import { useBottomBar, useStepData } from '../../store/selectors'
import { Subtitle, Title } from '../RecommendedPlans/styles'
import { DataWrapper, FlexContainer, YourPlansWrapper } from './styles'

const stepKey = 'yourPlans'

export const YourPlans = () => {
  const history = useHistory()
  const { dispatch } = useContext(GbsStore)
  const [show, setShow] = useState(false)
  const [currentSBCData, setSBCData] = useState<any>(null)
  const [sbcIsLoading, setSbcLoading] = useState(false)
  const toaster = useToaster()

  const bottomBarInfo = useBottomBar()

  const contribution = useStepData('contribution')
  const recommendedPlansData = useStepData('recommendedPlans')

  const avgAge = recommendedPlansData?.avgAge
  const contributionValue = contribution?.value
  const dependentValue = contribution?.dependentContribution

  const { data, isLoading } = useQuery(
    ['yourPlans', avgAge, contributionValue, dependentValue],
    () => planService.getSelectablePlans(null),
    {
      cacheTime: 30000,
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false
    }
  )

  const [plans, setPlans] = useState<ISelectablePlan[]>(() => [])

  useEffect(() => {
    if (data) setPlans(data)
  }, [data])

  const { captureAnswer, isLoading: isCapturingLoading } = useCaptureValue(
    stepKey,
    {
      plans: plans
        .filter((p) => p.selected)
        .map((p) => ({
          plan_id: p.id
        })) as any
    }
  )

  const stepConfig: Omit<GbsStep, 'data'> = useMemo(() => {
    return {
      bottomBar: {
        buttons: [
          {
            text: 'Next',
            variant: 'primary',
            disabled: isLoading || isCapturingLoading,
            callback: async () => {
              if (!(await captureAnswer())) return
              history.push(Paths.finalQuote)
            }
          }
        ],
        staticEmployeesAndAge: true
      },
      topBar: {
        completedPercentage: 80,
        showEdit: true,
        backCallback: () => history.push(Paths.employees)
      }
    }
  }, [isLoading, history, captureAnswer, isCapturingLoading])

  useStep(stepKey, stepConfig, dispatch)

  const handlePlanSelect = (selected: boolean, id: string) => {
    const newPlans = [...plans]
    const modifiedPlanIndex = newPlans.findIndex((plan) => plan.id === id)
    newPlans[modifiedPlanIndex].selected = selected

    setPlans(newPlans)
  }

  const handleShowPlanDetails = async (id: string, plan: ISelectablePlan) => {
    try {
      setSbcLoading(true)
      setShow(true)
      const { events, faqs } = await planService.getSBC(id)

      setSBCData({
        benefits: events,
        faqs,
        plan,
        coverageInfo: {
          plan: {
            name: plan.name,
            metalTier: plan?.tier,
            type: plan?.type
          },
          coveragePeriod: {
            from: plan?.startDate,
            to: plan?.endDate
          },
          coverageFor: plan?.eligibleMember?.map(capitalize).join('/') //'Single/Family'
        }
      })
      setSbcLoading(false)
    } catch (e) {
      setSbcLoading(false)
      setShow(false)
      toaster.show({
        message: "We can't get the SBC information",
        icon: 'icon-cancel',
        type: 'danger'
      })
    }
  }

  return (
    <>
      <YourPlansWrapper>
        <Title>Your Plans</Title>
        <Subtitle>
          Based on your employee information, these premiums reflect more
          accurate benefit costs.{' '}
        </Subtitle>
        <FlexContainer>
          <DataWrapper>Avg Age: {bottomBarInfo?.info?.avgAge}</DataWrapper>
          <DataWrapper>
            Emp #: {bottomBarInfo?.info?.numberOfEmployees}
          </DataWrapper>
          <DataWrapper>Zip: {bottomBarInfo?.info?.zip}</DataWrapper>
          <DataWrapper>
            {'Contribution: '}
            {bottomBarInfo?.info?.contribution?.type === Contribution.DOLLAR &&
              '$'}
            {bottomBarInfo?.info?.contribution?.value}
            {bottomBarInfo?.info?.contribution?.type ===
              Contribution.PERCENTAGE && '%'}
          </DataWrapper>
        </FlexContainer>
        {isLoading ? (
          <CenteredSpinner />
        ) : (
          <PlanList
            plans={plans}
            onPlanSelect={handlePlanSelect}
            onShowPlanDetails={handleShowPlanDetails}
            nonSelectedOpacity={0.5}
          />
        )}
      </YourPlansWrapper>
      <Modal
        show={show}
        onHide={() => setShow(false)}
        dialogClassName="w-100 m-0 mw-100"
        className="px-0"
        aria-labelledby="example-custom-modal-styling-title"
        style={{ zIndex: 9999 }}
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body style={{ minHeight: '95vh' }}>
          {sbcIsLoading && <CenteredSpinner />}
          {currentSBCData && !sbcIsLoading && (
            <SummaryOfBenefitsCoverage
              coverageInfo={currentSBCData.coverageInfo}
              benefits={currentSBCData.benefits}
              faqs={currentSBCData.faqs}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}

export default YourPlans
